'use client';

import { ReactNode } from 'react';
import { DrawerClose } from '@components/ui/drawer';
import { cn } from '@utils/cn';
import { BottomSheet } from './bottom-sheet';

export interface ActionSheetAction {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  destructive?: boolean;
  /** Shown but not selectable; pair with `description` to say why. */
  disabled?: boolean;
  /** Secondary line under the label (e.g. why an action is unavailable). */
  description?: ReactNode;
  onSelect: () => void;
}

export interface ActionSheetGroup {
  id: string;
  actions: ActionSheetAction[];
}

interface ActionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  groups: ActionSheetGroup[];
}

/**
 * iOS-style action sheet on top of BottomSheet. Mobile replacement for
 * DropdownMenu on create/overflow affordances.
 */
export function ActionSheet({
  open,
  onOpenChange,
  title,
  groups
}: ActionSheetProps) {
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={title}>
      <div className="flex flex-col gap-3 px-4 pb-4">
        {groups.map(group => (
          <div
            key={group.id}
            className="overflow-hidden rounded-md bg-muted/50"
          >
            {group.actions.map((action, i) => (
              <DrawerClose asChild key={action.id}>
                <button
                  type="button"
                  onClick={action.onSelect}
                  disabled={action.disabled}
                  className={cn(
                    'flex min-h-[var(--touch-target-min)] w-full flex-col items-center justify-center px-4 text-body transition-colors duration-fast ease-out active:bg-muted disabled:active:bg-transparent',
                    action.description && 'py-2.5',
                    i > 0 && 'border-t border-border/50',
                    action.destructive ? 'text-destructive' : 'text-foreground'
                  )}
                >
                  <span
                    className={cn(
                      'flex items-center justify-center gap-2',
                      action.disabled && 'opacity-50'
                    )}
                  >
                    {action.icon}
                    {action.label}
                  </span>
                  {action.description ? (
                    <span className="mt-0.5 text-caption text-muted-foreground">
                      {action.description}
                    </span>
                  ) : null}
                </button>
              </DrawerClose>
            ))}
          </div>
        ))}
      </div>
    </BottomSheet>
  );
}
